/*
  REQUIRES: pdfjs-dist
  npm i pdfjs-dist
*/
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// PDF.js requires an explicit worker URL in the browser. Use the exact
// installed PDF.js version so CRA/Webpack does not need import.meta.url
// or a separately copied worker file.

import {
  Upload,
  Download,
  RotateCcw,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ScanLine,
  Layers,
  Ruler,
  ChevronLeft,
  ChevronRight,
  X,
  Grid3x3,
  Circle as CircleIcon,
  Square,
} from "lucide-react";
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// PDF.js rasterizes imported PDF pages in the browser. The exported kiss-cut
// remains a separate PDF vector path.

/* =========================================================================
   KISS-CUT ENGINE
   Pure functions — mirror the pipeline in the spec:
   mask -> denoise -> morphological close -> external contour ->
   simplify -> true offset -> SVG path
   ========================================================================= */

const WORK_MAX_DIM = 560; // downsample for processing speed; output is rescaled

function loadWorkingCanvas(img) {
  const scale = Math.min(1, WORK_MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(8, Math.round(img.naturalWidth * scale));
  const h = Math.max(8, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  return { imageData: ctx.getImageData(0, 0, w, h), w, h, scale };
}

function detectHasAlpha(imageData) {
  const d = imageData.data;
  for (let i = 3; i < d.length; i += 4) {
    if (d[i] < 250) return true;
  }
  return false;
}

// --- 1. mask creation -----------------------------------------------------

function buildAlphaMask(imageData, w, h, alphaThreshold) {
  const d = imageData.data;
  const mask = new Uint8Array(w * h);
  for (let i = 3, p = 0; p < w * h; i += 4, p++) {
    mask[p] = d[i] > alphaThreshold ? 1 : 0;
  }
  return mask;
}

// Background removal that only strips *connected* background reaching the
// edges (step 3 in the spec) — white pixels trapped inside artwork survive.
function buildBackgroundMask(imageData, w, h, threshold) {
  const d = imageData.data;
  const corners = [
    [0, 0],
    [w - 1, 0],
    [0, h - 1],
    [w - 1, h - 1],
  ];
  let r = 0,
    g = 0,
    b = 0;
  corners.forEach(([x, y]) => {
    const idx = (y * w + x) * 4;
    r += d[idx];
    g += d[idx + 1];
    b += d[idx + 2];
  });
  r /= 4;
  g /= 4;
  b /= 4;

  const colorDist = (idx) => {
    const dr = d[idx] - r,
      dg = d[idx + 1] - g,
      db = d[idx + 2] - b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  const bg = new Uint8Array(w * h);
  const visited = new Uint8Array(w * h);
  const stack = [];
  for (let x = 0; x < w; x++) {
    stack.push(x, 0, x, h - 1);
  }
  for (let y = 0; y < h; y++) {
    stack.push(0, y, w - 1, y);
  }

  while (stack.length) {
    const y = stack.pop();
    const x = stack.pop();
    if (x < 0 || y < 0 || x >= w || y >= h) continue;
    const p = y * w + x;
    if (visited[p]) continue;
    visited[p] = 1;
    if (colorDist(p * 4) <= threshold) {
      bg[p] = 1;
      stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
    }
  }

  const mask = new Uint8Array(w * h);
  for (let p = 0; p < w * h; p++) mask[p] = bg[p] ? 0 : 1;
  return mask;
}

// --- connected components (used for noise removal + picking the sticker) -

function labelComponents(mask, w, h) {
  const labels = new Int32Array(w * h).fill(-1);
  const areas = [];
  let current = 0;
  const stack = [];
  for (let start = 0; start < w * h; start++) {
    if (mask[start] !== 1 || labels[start] !== -1) continue;
    let area = 0;
    stack.push(start);
    labels[start] = current;
    while (stack.length) {
      const p = stack.pop();
      area++;
      const x = p % w,
        y = (p / w) | 0;
      if (x + 1 < w) {
        const np = p + 1;
        if (mask[np] === 1 && labels[np] === -1) {
          labels[np] = current;
          stack.push(np);
        }
      }
      if (x - 1 >= 0) {
        const np = p - 1;
        if (mask[np] === 1 && labels[np] === -1) {
          labels[np] = current;
          stack.push(np);
        }
      }
      if (y + 1 < h) {
        const np = p + w;
        if (mask[np] === 1 && labels[np] === -1) {
          labels[np] = current;
          stack.push(np);
        }
      }
      if (y - 1 >= 0) {
        const np = p - w;
        if (mask[np] === 1 && labels[np] === -1) {
          labels[np] = current;
          stack.push(np);
        }
      }
    }
    areas.push(area);
    current++;
  }
  return { labels, areas, count: current };
}

function removeSmallComponents(mask, w, h, minAreaPx) {
  const { labels, areas, count } = labelComponents(mask, w, h);
  const keep = new Uint8Array(count);
  for (let i = 0; i < count; i++) keep[i] = areas[i] >= minAreaPx ? 1 : 0;
  const out = new Uint8Array(w * h);
  for (let p = 0; p < w * h; p++) {
    const l = labels[p];
    if (l >= 0 && keep[l]) out[p] = 1;
  }
  return out;
}

// --- 2. morphological closing (join nearby artwork into one silhouette) --

function dilate(mask, w, h, radius) {
  if (radius <= 0) return mask.slice();
  const out = new Uint8Array(w * h);
  const r2 = radius * radius;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x] !== 1) continue;
      for (let dy = -radius; dy <= radius; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= h) continue;
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy > r2) continue;
          const nx = x + dx;
          if (nx < 0 || nx >= w) continue;
          out[ny * w + nx] = 1;
        }
      }
    }
  }
  return out;
}

function erode(mask, w, h, radius) {
  if (radius <= 0) return mask.slice();
  const out = new Uint8Array(w * h);
  const r2 = radius * radius;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let keep = 1;
      for (let dy = -radius; dy <= radius && keep; dy++) {
        const ny = y + dy;
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy > r2) continue;
          const nx = x + dx;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h || mask[ny * w + nx] !== 1) {
            keep = 0;
            break;
          }
        }
      }
      out[y * w + x] = keep;
    }
  }
  return out;
}

function morphClose(mask, w, h, radius) {
  return erode(dilate(mask, w, h, radius), w, h, radius);
}

// --- 3. external contour (Moore-neighbor boundary tracing) ---------------

const NBR = [
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
]; // N, NE, E, SE, S, SW, W, NW — clockwise

function offsetIndex(dx, dy) {
  for (let i = 0; i < 8; i++) if (NBR[i][0] === dx && NBR[i][1] === dy) return i;
  return 6;
}

function traceBoundary(mask, w, h, sx, sy) {
  const isFg = (x, y) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 1;
  const start = [sx, sy];
  let P = start;
  let B = [sx - 1, sy]; // west of the topmost-leftmost pixel is guaranteed background
  const contour = [P];
  const maxSteps = w * h * 4;

  for (let steps = 0; steps < maxSteps; steps++) {
    const startIdx = offsetIndex(B[0] - P[0], B[1] - P[1]);
    let found = null;
    let prevBg = B;
    for (let i = 1; i <= 8; i++) {
      const idx = (startIdx + i) % 8;
      const [ox, oy] = NBR[idx];
      const nx = P[0] + ox,
        ny = P[1] + oy;
      if (isFg(nx, ny)) {
        found = [nx, ny];
        break;
      }
      prevBg = [nx, ny];
    }
    if (!found) break; // isolated pixel — nothing to trace
    B = prevBg;
    P = found;
    contour.push(P);
    if (steps > 0 && P[0] === start[0] && P[1] === start[1]) break;
  }
  return contour;
}

function findLargestComponentSeed(mask, w, h) {
  const { labels, areas, count } = labelComponents(mask, w, h);
  if (count === 0) return null;
  let best = 0;
  for (let i = 1; i < count; i++) if (areas[i] > areas[best]) best = i;
  for (let p = 0; p < w * h; p++) {
    if (labels[p] === best) {
      return { x: p % w, y: (p / w) | 0, area: areas[best] };
    }
  }
  return null;
}

// --- 4. simplify (Douglas–Peucker) ----------------------------------------

function perpDist(pt, a, b) {
  const [x, y] = pt,
    [x1, y1] = a,
    [x2, y2] = b;
  const dx = x2 - x1,
    dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return Math.hypot(x - x1, y - y1);
  const t = ((x - x1) * dx + (y - y1) * dy) / (len * len);
  const px = x1 + t * dx,
    py = y1 + t * dy;
  return Math.hypot(x - px, y - py);
}

function douglasPeucker(points, epsilon) {
  if (points.length < 3) return points.slice();
  let maxDist = 0,
    index = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpDist(points[i], points[0], points[points.length - 1]);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }
  if (maxDist > epsilon) {
    const left = douglasPeucker(points.slice(0, index + 1), epsilon);
    const right = douglasPeucker(points.slice(index), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[points.length - 1]];
}

function simplifyClosed(points, epsilon) {
  if (points.length < 4) return points.slice();
  const closed =
    points[0][0] === points[points.length - 1][0] && points[0][1] === points[points.length - 1][1]
      ? points
      : points.concat([points[0]]);
  const simplified = douglasPeucker(closed, epsilon);
  if (simplified.length > 1) {
    const f = simplified[0],
      l = simplified[simplified.length - 1];
    if (f[0] === l[0] && f[1] === l[1]) simplified.pop();
  }
  return simplified;
}

// --- 5. true geometric offset (edge-normal + intersection, mitered) ------

function signedArea(points) {
  let a = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

function lineIntersect(p1, p2, p3, p4) {
  const [x1, y1] = p1,
    [x2, y2] = p2,
    [x3, y3] = p3,
    [x4, y4] = p4;
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-9) return null;
  const a = x1 * y2 - y1 * x2;
  const b = x3 * y4 - y3 * x4;
  const px = (a * (x3 - x4) - (x1 - x2) * b) / denom;
  const py = (a * (y3 - y4) - (y1 - y2) * b) / denom;
  return [px, py];
}

function offsetPolygon(points, distance) {
  const n = points.length;
  if (n < 3 || distance === 0) return points.slice();
  const sign = signedArea(points) >= 0 ? 1 : -1;

  const edges = [];
  for (let i = 0; i < n; i++) {
    const p1 = points[i],
      p2 = points[(i + 1) % n];
    let dx = p2[0] - p1[0],
      dy = p2[1] - p1[1];
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const nx = dy * sign,
      ny = -dx * sign; // outward unit normal
    edges.push({
      p1: [p1[0] + nx * distance, p1[1] + ny * distance],
      p2: [p2[0] + nx * distance, p2[1] + ny * distance],
    });
  }

  const miterLimit = Math.abs(distance) * 1.5;
  const result = [];
  for (let i = 0; i < n; i++) {
    const eA = edges[(i - 1 + n) % n];
    const eB = edges[i];
    const ip = lineIntersect(eA.p1, eA.p2, eB.p1, eB.p2);
    const orig = points[i];
    if (ip && Math.hypot(ip[0] - orig[0], ip[1] - orig[1]) <= miterLimit) {
      result.push(ip);
    } else {
      result.push([(eA.p2[0] + eB.p1[0]) / 2, (eA.p2[1] + eB.p1[1]) / 2]); // bevel fallback
    }
  }
  return result;
}

// --- misc helpers -----------------------------------------------------------

function polygonArea(points) {
  return Math.abs(signedArea(points));
}

function minEdgeLength(points) {
  let min = Infinity;
  for (let i = 0; i < points.length; i++) {
    const a = points[i],
      b = points[(i + 1) % points.length];
    min = Math.min(min, Math.hypot(a[0] - b[0], a[1] - b[1]));
  }
  return min;
}

function segmentsIntersect(p1, p2, p3, p4) {
  const d = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const d1 = d(p3, p4, p1),
    d2 = d(p3, p4, p2),
    d3 = d(p1, p2, p3),
    d4 = d(p1, p2, p4);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

function hasSelfIntersection(points) {
  const n = points.length;
  if (n > 400) return false; // skip on very dense paths — too costly, treat as unverified/pass
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue;
      if (
        segmentsIntersect(
          points[i],
          points[(i + 1) % n],
          points[j],
          points[(j + 1) % n]
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

function polygonToPath(points) {
  if (!points.length) return "";
  const pts = points.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`);
  return `M ${pts.join(" L ")} Z`;
}

/* =========================================================================
   MAIN PIPELINE (per image)
   ========================================================================= */

function generateKissCut(img, params) {
  const { imageData, w, h, scale } = loadWorkingCanvas(img);
  const hasAlpha = detectHasAlpha(imageData);
  const mode = hasAlpha ? "ALPHA" : "BACKGROUND";

  let mask =
    mode === "ALPHA"
      ? buildAlphaMask(imageData, w, h, 20)
      : buildBackgroundMask(imageData, w, h, params.bgThreshold);

  const minAreaPx = Math.max(4, Math.round(w * h * 0.00025));
  mask = removeSmallComponents(mask, w, h, minAreaPx);

  const rawArtworkPx = mask.reduce((s, v) => s + v, 0);
  if (rawArtworkPx === 0) {
    throw new Error(
      "No object detected — the whole canvas reads as background. Try lowering the background threshold."
    );
  }

  if (params.joinDistance > 0) {
    mask = morphClose(mask, w, h, params.joinDistance);
  }

  const seed = findLargestComponentSeed(mask, w, h);
  if (!seed) {
    throw new Error("Contour could not be traced from the mask.");
  }

  let raw = traceBoundary(mask, w, h, seed.x, seed.y);
  if (raw.length < 3) {
    throw new Error("Traced boundary was degenerate (fewer than 3 points).");
  }

  const epsilon = Math.max(0.4, params.smoothing * 1.1);
  let simplified = simplifyClosed(raw, epsilon);
  if (simplified.length < 3) simplified = raw;

  const offsetPxWork = ((params.offsetMm / 25.4) * params.dpi) * scale;
  const offset = offsetPolygon(simplified, offsetPxWork);

  const artworkArea = polygonArea(simplified);
  const cutArea = polygonArea(offset);
  const minEdge = minEdgeLength(offset);
  const selfX = hasSelfIntersection(offset);

  const quality = [
    { label: "Contour found", pass: simplified.length >= 3 },
    { label: "Contour is closed", pass: true },
    { label: "Contour area is valid", pass: artworkArea > minAreaPx },
    {
      label: "Kiss-cut offset is valid",
      pass: Number.isFinite(params.offsetMm) && Math.abs(offsetPxWork) <= Math.max(1, Math.min(w, h) * 0.45),
    },
    { label: "No self-intersection", pass: !selfX },
    {
      label: "Offset direction maintained",
      pass: params.offsetMm >= 0 ? cutArea >= artworkArea * 0.995 : cutArea <= artworkArea * 1.005,
    },
    { label: "No extremely tiny segments", pass: minEdge > 0.6 },
  ];

  const offsetImgPts = offset.map(([x, y]) => [x / scale, y / scale]);
  const artworkImgPts = simplified.map(([x, y]) => [x / scale, y / scale]);

  return {
    mode,
    pathData: polygonToPath(offsetImgPts),
    artworkPathData: polygonToPath(artworkImgPts),
    offsetPointsImgSpace: offsetImgPts,
    artworkPointsImgSpace: artworkImgPts,
    pointCount: offset.length,
    quality,
    ok: quality.every((q) => q.pass),
    workDims: { w, h, scale },
  };
}

/* =========================================================================
   CROP TO CUTLINE
   ========================================================================= */

function cropToCutline(img, result, marginPx) {
  const pts = result.offsetPointsImgSpace;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const [x, y] of pts) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  // IMPORTANT:
  // Do NOT clamp the requested margin to the source image bounds.
  // The old implementation only enlarged the crop when pixels already
  // existed outside the cut line. It did not CREATE blank space.
  // This version creates a real white margin around the kiss-cut.
  const safeMargin = Math.max(0, Number(marginPx) || 0);

  const cropMinX = Math.floor(minX - safeMargin);
  const cropMinY = Math.floor(minY - safeMargin);
  const cropMaxX = Math.ceil(maxX + safeMargin);
  const cropMaxY = Math.ceil(maxY + safeMargin);

  const cropW = Math.max(1, cropMaxX - cropMinX);
  const cropH = Math.max(1, cropMaxY - cropMinY);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");

  // Real blank/white margin.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, cropW, cropH);

  // Only draw the portion that actually exists inside the source image.
  const srcMinX = Math.max(0, cropMinX);
  const srcMinY = Math.max(0, cropMinY);
  const srcMaxX = Math.min(img.width, cropMaxX);
  const srcMaxY = Math.min(img.height, cropMaxY);

  const srcW = Math.max(0, srcMaxX - srcMinX);
  const srcH = Math.max(0, srcMaxY - srcMinY);

  if (srcW > 0 && srcH > 0) {
    const dstX = srcMinX - cropMinX;
    const dstY = srcMinY - cropMinY;

    ctx.drawImage(
      img.element,
      srcMinX,
      srcMinY,
      srcW,
      srcH,
      dstX,
      dstY,
      srcW,
      srcH
    );
  }

  // Shift the vector kiss-cut and artwork paths by the same amount as
  // the cropped image, so the vector cut remains perfectly aligned.
  const shift = ([x, y]) => [x - cropMinX, y - cropMinY];
  const offsetPointsImgSpace = pts.map(shift);
  const artworkPointsImgSpace = result.artworkPointsImgSpace.map(shift);

  return {
    canvas,
    url: canvas.toDataURL("image/png"),
    width: cropW,
    height: cropH,
    cropOrigin: { x: cropMinX, y: cropMinY },
    marginPx: safeMargin,
    offsetPointsImgSpace,
    artworkPointsImgSpace,
    pathData: polygonToPath(offsetPointsImgSpace),
    artworkPathData: polygonToPath(artworkPointsImgSpace),
  };
}

function buildRender(img, params) {
  const result = generateKissCut(img.element, params);
  // Border margin is authored in millimeters (same real-world unit as the
  // kiss-cut offset) so it stays meaningful regardless of the source
  // image's pixel resolution. Convert using the same mm -> image-px
  // formula used for the offset itself (image is assumed to be at
  // params.dpi pixels-per-inch), so a "2mm" margin looks like 2mm on
  // every upload, not a fixed handful of pixels on a multi-megapixel photo.
  const marginPx = (params.borderMargin / 25.4) * params.dpi;
  const crop = cropToCutline(img, result, marginPx);
  return { ...result, ...crop };
}

/* =========================================================================
   MINIMAL PDF WRITER — single-image cut-ready PDF (unchanged pipeline)
   ========================================================================= */

function imageToPdfPoint(x, y, dpi, pageHpt) {
  return [(x / dpi) * 72, pageHpt - (y / dpi) * 72];
}

async function buildKissCutPdf(result, params) {
  const canvas = document.createElement("canvas");
  canvas.width = result.width;
  canvas.height = result.height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(result.canvas, 0, 0);
  const jpegBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());

  const dpi = params.dpi;
  const pageWpt = (result.width / dpi) * 72;
  const pageHpt = (result.height / dpi) * 72;

  const pts = result.offsetPointsImgSpace;
  const pathOps = pts
    .map(([x, y], i) => {
      const [px, py] = imageToPdfPoint(x, y, dpi, pageHpt);
      return `${px.toFixed(2)} ${py.toFixed(2)} ${i === 0 ? "m" : "l"}`;
    })
    .join("\n");

  const content = `q
${pageWpt.toFixed(3)} 0 0 ${pageHpt.toFixed(3)} 0 0 cm
/Im0 Do
Q
q
1 J 1 j
1 0 1 RG
0.75 w
${pathOps}
h
S
Q
`;

  const enc = new TextEncoder();
  const parts = [];
  let length = 0;
  const offsets = [0];
  const push = (bytes) => {
    parts.push(bytes);
    length += bytes.length;
  };
  const pushStr = (s) => push(enc.encode(s));

  pushStr("%PDF-1.4\n");
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  offsets[1] = length;
  pushStr("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");

  offsets[2] = length;
  pushStr("2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n");

  offsets[3] = length;
  pushStr(
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWpt.toFixed(3)} ${pageHpt.toFixed(
      3
    )}] /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>\nendobj\n`
  );

  offsets[4] = length;
  const contentBytes = enc.encode(content);
  pushStr(`4 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n`);
  push(contentBytes);
  pushStr("\nendstream\nendobj\n");

  offsets[5] = length;
  pushStr(
    `5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${result.width} /Height ${result.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`
  );
  push(jpegBytes);
  pushStr("\nendstream\nendobj\n");

  const xrefOffset = length;
  let xref = "xref\n0 6\n0000000000 65535 f \n";
  for (let i = 1; i <= 5; i++) {
    xref += String(offsets[i]).padStart(10, "0") + " 00000 n \n";
  }
  pushStr(xref);
  pushStr(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(parts, { type: "application/pdf" });
}

/* =========================================================================
   SHEET / GRID CONFIGS
   ========================================================================= */

const DIGITAL_CONFIG = { GRID_COLS: 5, GRID_ROWS: 5, GRID_WIDTH: 90.5, GRID_HEIGHT: 58, TOTAL_LABELS: 25, PDF_WIDTH: 460, PDF_HEIGHT: 305 };
const OFFSET_CONFIG = { GRID_COLS: 5, GRID_ROWS: 6, GRID_WIDTH: 90.5, GRID_HEIGHT: 58, TOTAL_LABELS: 30, PDF_WIDTH: 510, PDF_HEIGHT: 380 };
const ROUND_DIGITAL_CONFIG = { GRID_COLS: 7, GRID_ROWS: 5, STICKER_DIAMETER: 58, TOTAL_LABELS: 35, PDF_WIDTH: 460, PDF_HEIGHT: 305 };
const ROUND_OFFSET_CONFIG = { GRID_COLS: 8, GRID_ROWS: 6, STICKER_DIAMETER: 58, TOTAL_LABELS: 48, PDF_WIDTH: 510, PDF_HEIGHT: 380 };

// All sheet pages are always landscape (width > height). Guard defensively
// in case a config is ever edited to a portrait pair.
function landscapeDims(wMm, hMm) {
  return wMm >= hMm ? { w: wMm, h: hMm } : { w: hMm, h: wMm };
}

const GRID_CONFIGS = {
  rect: { digital: DIGITAL_CONFIG, offset: OFFSET_CONFIG },
  round: { digital: ROUND_DIGITAL_CONFIG, offset: ROUND_OFFSET_CONFIG },
};

// Split TOTAL_LABELS slots across N uploaded images, as evenly as possible.
// Filled row-major, image 0 first — with 2 images and an even grid this
// reads as roughly "top half / bottom half" of the sheet.
function distributeSlots(imageIds, totalLabels) {
  const n = imageIds.length;
  if (n === 0) return { slots: [], counts: [] };
  const base = Math.floor(totalLabels / n);
  const remainder = totalLabels % n;
  const counts = imageIds.map((id, i) => ({ id, count: base + (i < remainder ? 1 : 0) }));
  const slots = [];
  counts.forEach(({ id, count }) => {
    for (let i = 0; i < count; i++) slots.push(id);
  });
  return { slots, counts };
}

// Grid cell borders are intentionally never drawn — only placed artwork and
// its kiss-cut line render on the sheet.
const SHEET_RASTER_DPI_CAP = 300;

/* =========================================================================
   SHEET / VECTOR PDF EXPORT
   The sheet artwork is rasterized only as an image.
   Kiss-cut contours remain real PDF vector paths so they are selectable
   and editable in Inkscape / Illustrator.
   ========================================================================= */

function circlePath(cx, cy, r) {
  const k = 0.5522847498307936;
  return [
    `M ${(cx + r).toFixed(4)} ${cy.toFixed(4)}`,
    `C ${(cx + r).toFixed(4)} ${(cy + k * r).toFixed(4)} ${(cx + k * r).toFixed(4)} ${(cy + r).toFixed(4)} ${cx.toFixed(4)} ${(cy + r).toFixed(4)}`,
    `C ${(cx - k * r).toFixed(4)} ${(cy + r).toFixed(4)} ${(cx - r).toFixed(4)} ${(cy + k * r).toFixed(4)} ${(cx - r).toFixed(4)} ${cy.toFixed(4)}`,
    `C ${(cx - r).toFixed(4)} ${(cy - k * r).toFixed(4)} ${(cx - k * r).toFixed(4)} ${(cy - r).toFixed(4)} ${cx.toFixed(4)} ${(cy - r).toFixed(4)}`,
    `C ${(cx + k * r).toFixed(4)} ${(cy - r).toFixed(4)} ${(cx + r).toFixed(4)} ${(cy - k * r).toFixed(4)} ${(cx + r).toFixed(4)} ${cy.toFixed(4)} Z`,
  ].join(" ");
}

function circlePoints(cx, cy, r, count = 96) {
  return Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  });
}

function buildSheetSVG(imgs, results, config, shape, params, includeCut = true) {
  const {
    GRID_COLS,
    GRID_ROWS,
    TOTAL_LABELS,
    GRID_WIDTH,
    GRID_HEIGHT,
    STICKER_DIAMETER,
  } = config;

  const { w: PDF_WIDTH, h: PDF_HEIGHT } = landscapeDims(
    config.PDF_WIDTH,
    config.PDF_HEIGHT
  );

  const cellW = shape === "round" ? STICKER_DIAMETER : GRID_WIDTH;
  const cellH = shape === "round" ? STICKER_DIAMETER : GRID_HEIGHT;
  const contentW = GRID_COLS * cellW;
  const contentH = GRID_ROWS * cellH;
  const marginX = (PDF_WIDTH - contentW) / 2;
  const marginY = (PDF_HEIGHT - contentH) / 2;
  const pxToMm = 25.4 / params.dpi;

  const ids = imgs
    .map((im) => im.id)
    .filter((id) => results[id]?.status === "ready");

  const { slots } = distributeSlots(ids, TOTAL_LABELS);

  let defs = "";
  let cells = "";

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      const slotIdx = row * GRID_COLS + col;
      const cellX = marginX + col * cellW;
      const cellY = marginY + row * cellH;
      const imgId = slots[slotIdx];

      if (shape === "round") {
        const clipId = `clip-${row}-${col}`;
        defs += `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse">
          <circle cx="${(cellX + cellW / 2).toFixed(2)}" cy="${(cellY + cellH / 2).toFixed(2)}" r="${(cellW / 2).toFixed(2)}" />
        </clipPath>`;
      }

      if (!imgId || !results[imgId] || results[imgId].status !== "ready") {
        continue;
      }

      const r = results[imgId].data;
      const sourceImage = imgs.find((im) => im.id === imgId);

      // ROUND ONLY: use the original source artwork, not the auto-cropped
      // contour result. This is important for round stickers because the
      // circular sticker itself is the finished shape. The source is clipped
      // to that circle; rectangular mode keeps the existing cropped pipeline.
      const sourceW = shape === "round" && sourceImage?.width ? sourceImage.width : r.width;
      const sourceH = shape === "round" && sourceImage?.height ? sourceImage.height : r.height;
      const sourceUrl = shape === "round" && sourceImage?.url ? sourceImage.url : r.url;

      const contentWmm = sourceW * pxToMm;
      const contentHmm = sourceH * pxToMm;

      const fit = Math.min(
        (cellW * (shape === "round" ? 1 : 0.9)) / contentWmm,
        (cellH * (shape === "round" ? 1 : 0.9)) / contentHmm
      );

      const s = pxToMm * fit;

      const tx = cellX + cellW / 2 - (sourceW * s) / 2;
      const ty = cellY + cellH / 2 - (sourceH * s) / 2;

      const clipAttr =
        shape === "round"
          ? ` clip-path="url(#clip-${row}-${col})"`
          : "";

      cells += `<g
        transform="translate(${tx.toFixed(4)} ${ty.toFixed(4)}) scale(${s.toFixed(6)})"${clipAttr}
      >`;

      // IMPORTANT:
      // The artwork is rasterized into the PDF image.
      // The kiss-cut path is NOT rasterized into that image.
      cells += `<image
        href="${sourceUrl}"
        x="0"
        y="0"
        width="${sourceW}"
        height="${sourceH}"
      />`;

      if (includeCut) {
        if (shape === "round") {
          // ROUND ONLY: export the EXACT contour identified by the detector.
          // buildRender() crops the artwork, so restore the crop origin before
          // mapping the detected points onto the original source image. This
          // keeps the previewed/identified kiss-cut and the PDF vector path
          // on exactly the same geometry.
          const originX = Number(r.cropOrigin?.x || 0);
          const originY = Number(r.cropOrigin?.y || 0);
          const detectedPoints = Array.isArray(r.offsetPointsImgSpace)
            ? r.offsetPointsImgSpace.map(([x, y]) => [x + originX, y + originY])
            : [];
          if (detectedPoints.length >= 3) {
            cells += `<path d="${polygonToPath(detectedPoints)}" fill="none" stroke="#FF00FF" stroke-width="${(0.20 / s).toFixed(6)}" stroke-linejoin="round" stroke-linecap="round" />`;
          }
        } else {
          cells += `<path d="${r.pathData}" fill="none" stroke="#FF00FF" stroke-width="${(0.20 / s).toFixed(6)}" stroke-linejoin="round" stroke-linecap="round" />`;
        }
      }

      cells += `</g>`;
    }
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg
  xmlns="http://www.w3.org/2000/svg"
  xmlns:xlink="http://www.w3.org/1999/xlink"
  width="${PDF_WIDTH}mm"
  height="${PDF_HEIGHT}mm"
  viewBox="0 0 ${PDF_WIDTH} ${PDF_HEIGHT}"
>
  <defs>${defs}</defs>
  <rect
    x="0"
    y="0"
    width="${PDF_WIDTH}"
    height="${PDF_HEIGHT}"
    fill="#ffffff"
  />
  ${cells}
</svg>`;
}

/*
 * Build the exact vector kiss-cut geometry used by the sheet.
 *
 * Coordinates returned here are in PDF points, already accounting for:
 *   - cell position
 *   - artwork fit/scale
 *   - image crop coordinates
 *   - PDF's bottom-left coordinate system
 *
 * This MUST use the same tx/ty/s values as buildSheetSVG().
 */
function buildSheetVectorCutPaths(imgs, results, config, shape, params) {
  const {
    GRID_COLS,
    GRID_ROWS,
    TOTAL_LABELS,
    GRID_WIDTH,
    GRID_HEIGHT,
    STICKER_DIAMETER,
  } = config;

  const { w: PDF_WIDTH, h: PDF_HEIGHT } = landscapeDims(
    config.PDF_WIDTH,
    config.PDF_HEIGHT
  );

  const cellW = shape === "round" ? STICKER_DIAMETER : GRID_WIDTH;
  const cellH = shape === "round" ? STICKER_DIAMETER : GRID_HEIGHT;
  const contentW = GRID_COLS * cellW;
  const contentH = GRID_ROWS * cellH;
  const marginX = (PDF_WIDTH - contentW) / 2;
  const marginY = (PDF_HEIGHT - contentH) / 2;
  const pxToMm = 25.4 / params.dpi;

  const ids = imgs
    .map((im) => im.id)
    .filter((id) => results[id]?.status === "ready");

  const { slots } = distributeSlots(ids, TOTAL_LABELS);

  const mmToPt = 72 / 25.4;
  const paths = [];

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      const slotIdx = row * GRID_COLS + col;
      const cellX = marginX + col * cellW;
      const cellY = marginY + row * cellH;
      const imgId = slots[slotIdx];

      if (!imgId || !results[imgId] || results[imgId].status !== "ready") {
        continue;
      }

      const r = results[imgId].data;
      const sourceImage = imgs.find((im) => im.id === imgId);
      const sourceW = shape === "round" && sourceImage?.width ? sourceImage.width : r.width;
      const sourceH = shape === "round" && sourceImage?.height ? sourceImage.height : r.height;

      const contentWmm = sourceW * pxToMm;
      const contentHmm = sourceH * pxToMm;

      const fit = Math.min(
        (cellW * (shape === "round" ? 1 : 0.9)) / contentWmm,
        (cellH * (shape === "round" ? 1 : 0.9)) / contentHmm
      );

      const s = pxToMm * fit;

      const tx = cellX + cellW / 2 - (sourceW * s) / 2;
      const ty = cellY + cellH / 2 - (sourceH * s) / 2;

      let sourcePoints;
      if (shape === "round") {
        // ROUND ONLY: use the same detected kiss-cut points used by the
        // artwork result, restored to original source-image coordinates.
        // This makes the PDF vector cut exactly match the identified cut.
        const originX = Number(r.cropOrigin?.x || 0);
        const originY = Number(r.cropOrigin?.y || 0);
        if (!Array.isArray(r.offsetPointsImgSpace) || r.offsetPointsImgSpace.length < 3) continue;
        sourcePoints = r.offsetPointsImgSpace.map(([x, y]) => [x + originX, y + originY]);
      } else {
        if (!Array.isArray(r.offsetPointsImgSpace) || r.offsetPointsImgSpace.length < 3) continue;
        sourcePoints = r.offsetPointsImgSpace;
      }

      const points = sourcePoints.map(([x, y]) => {
        // Image coordinate -> sheet mm
        const xMm = tx + x * s;
        const yMm = ty + y * s;

        // Sheet mm -> PDF points, with Y flipped.
        return [
          xMm * mmToPt,
          PDF_HEIGHT * mmToPt - yMm * mmToPt,
        ];
      });

      paths.push({
        points,
        row,
        col,
        imageId: imgId,
      });
    }
  }

  return {
    pageWpt: PDF_WIDTH * mmToPt,
    pageHpt: PDF_HEIGHT * mmToPt,
    paths,
  };
}

/*
 * Rasterize ONLY the artwork.
 *
 * The SVG passed here has no kiss-cut stroke. Therefore the JPEG placed
 * inside the PDF contains no magenta cut pixels.
 */
function rasterizeSvgToCanvas(svgString, pxWidth, pxHeight) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = pxWidth;
      canvas.height = pxHeight;

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, pxWidth, pxHeight);

      ctx.drawImage(img, 0, 0, pxWidth, pxHeight);

      URL.revokeObjectURL(url);
      resolve(canvas);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not rasterize the sheet artwork."));
    };

    img.src = url;
  });
}

function buildPdfPathOps(points) {
  if (!points || points.length < 2) return "";

  return points
    .map(
      ([x, y], i) =>
        `${x.toFixed(3)} ${y.toFixed(3)} ${i === 0 ? "m" : "l"}`
    )
    .join("\n");
}

/*
 * pages: [{ imgs, config, shape }]
 *
 * Result:
 *   PDF page = raster artwork image + REAL VECTOR kiss-cut paths
 *
 * The kiss-cut paths are therefore selectable in Inkscape.
 */
async function buildSheetPagesPdf(pages, results, params) {
  const sheetDpi = Math.min(params.dpi, SHEET_RASTER_DPI_CAP);
  const pdfPages = [];

  for (const page of pages) {
    const { w: pageWmm, h: pageHmm } = landscapeDims(
      page.config.PDF_WIDTH,
      page.config.PDF_HEIGHT
    );

    // Render the sheet artwork directly to a canvas instead of rasterizing an
    // SVG containing nested data/object URLs. This is especially important for
    // ROUND mode: the original logo/image is clipped to a true circle here,
    // so the exported PDF cannot collapse the artwork into the small artifacts
    // seen in the previous round PDF.
    const pxWidth = Math.max(1, Math.round((pageWmm / 25.4) * sheetDpi));
    const pxHeight = Math.max(1, Math.round((pageHmm / 25.4) * sheetDpi));
    const canvas = document.createElement("canvas");
    canvas.width = pxWidth;
    canvas.height = pxHeight;

    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pxWidth, pxHeight);

    const { GRID_COLS, GRID_ROWS, TOTAL_LABELS, GRID_WIDTH, GRID_HEIGHT, STICKER_DIAMETER } = page.config;
    const cellW = page.shape === "round" ? STICKER_DIAMETER : GRID_WIDTH;
    const cellH = page.shape === "round" ? STICKER_DIAMETER : GRID_HEIGHT;
    const contentW = GRID_COLS * cellW;
    const contentH = GRID_ROWS * cellH;
    const marginX = (pageWmm - contentW) / 2;
    const marginY = (pageHmm - contentH) / 2;
    const pxPerMm = sheetDpi / 25.4;
    const pxToMm = 25.4 / params.dpi;

    const ids = page.imgs
      .map((im) => im.id)
      .filter((id) => results[id]?.status === "ready");
    const { slots } = distributeSlots(ids, TOTAL_LABELS);

    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const slotIdx = row * GRID_COLS + col;
        const imgId = slots[slotIdx];
        if (!imgId || !results[imgId] || results[imgId].status !== "ready") continue;

        const r = results[imgId].data;
        const sourceImage = page.imgs.find((im) => im.id === imgId);

        // ROUND ONLY uses the original source image. Rectangle mode preserves
        // the existing result.canvas pipeline exactly.
        const sourceEl = page.shape === "round" && sourceImage?.element ? sourceImage.element : r.canvas;
        const sourceW = page.shape === "round" && sourceImage?.width ? sourceImage.width : r.width;
        const sourceH = page.shape === "round" && sourceImage?.height ? sourceImage.height : r.height;

        const contentWmm = sourceW * pxToMm;
        const contentHmm = sourceH * pxToMm;
        const fit = Math.min(
          (cellW * (page.shape === "round" ? 1 : 0.9)) / contentWmm,
          (cellH * (page.shape === "round" ? 1 : 0.9)) / contentHmm
        );
        const drawWmm = sourceW * pxToMm * fit;
        const drawHmm = sourceH * pxToMm * fit;
        const cellXmm = marginX + col * cellW;
        const cellYmm = marginY + row * cellH;
        const drawXpx = (cellXmm + cellW / 2 - drawWmm / 2) * pxPerMm;
        const drawYpx = (cellYmm + cellH / 2 - drawHmm / 2) * pxPerMm;
        const drawWpx = drawWmm * pxPerMm;
        const drawHpx = drawHmm * pxPerMm;

        ctx.save();
        if (page.shape === "round") {
          const cx = (cellXmm + cellW / 2) * pxPerMm;
          const cy = (cellYmm + cellH / 2) * pxPerMm;
          const radiusPx = (STICKER_DIAMETER / 2) * pxPerMm;
          ctx.beginPath();
          ctx.arc(cx, cy, radiusPx, 0, Math.PI * 2);
          ctx.clip();
        }

        ctx.drawImage(sourceEl, drawXpx, drawYpx, drawWpx, drawHpx);
        ctx.restore();
      }
    }

    const vectorData = buildSheetVectorCutPaths(
      page.imgs,
      results,
      page.config,
      page.shape,
      params
    );

    pdfPages.push({
      canvas,
      pageWmm,
      pageHmm,
      pageWpt: vectorData.pageWpt,
      pageHpt: vectorData.pageHpt,
      vectorPaths: vectorData.paths,
    });
  }

  return buildMultiPagePdfFromCanvases(pdfPages);
}

/*
 * Hand-written PDF writer.
 *
 * Each page contains:
 *   1. JPEG artwork image
 *   2. Separate vector kiss-cut paths
 *
 * The vector paths use:
 *   RGB = 1 0 1  (magenta)
 *   line width = 0.20 mm
 *   round joins/caps
 */
async function buildMultiPagePdfFromCanvases(pages) {
  const enc = new TextEncoder();
  const parts = [];
  let length = 0;

  const push = (bytes) => {
    parts.push(bytes);
    length += bytes.length;
  };

  const pushStr = (s) => push(enc.encode(s));

  pushStr("%PDF-1.4\n");
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  const offsets = {};
  const totalPages = pages.length;
  const pageObjNums = [];

  for (let i = 0; i < totalPages; i++) {
    pageObjNums.push(3 + i * 3);
  }

  // Catalog
  offsets[1] = length;
  pushStr(
    "1 0 obj\n" +
      "<< /Type /Catalog /Pages 2 0 R >>\n" +
      "endobj\n"
  );

  // Pages
  offsets[2] = length;
  const kids = pageObjNums.map((n) => `${n} 0 R`).join(" ");

  pushStr(
    `2 0 obj\n` +
      `<< /Type /Pages /Kids [${kids}] /Count ${totalPages} >>\n` +
      `endobj\n`
  );

  for (let i = 0; i < totalPages; i++) {
    const pageNum = 3 + i * 3;
    const contentNum = 4 + i * 3;
    const imgNum = 5 + i * 3;

    const page = pages[i];

    const pageWpt =
      page.pageWpt ||
      (page.pageWmm / 25.4) * 72;

    const pageHpt =
      page.pageHpt ||
      (page.pageHmm / 25.4) * 72;

    // ------------------------------------------------------------
    // JPEG artwork
    // ------------------------------------------------------------
    const jpegBlob = await new Promise((resolve, reject) => {
      page.canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Could not encode sheet artwork."));
        },
        "image/jpeg",
        0.92
      );
    });

    const jpegBytes = new Uint8Array(
      await jpegBlob.arrayBuffer()
    );

    // ------------------------------------------------------------
    // PDF page object
    // ------------------------------------------------------------
    offsets[pageNum] = length;

    pushStr(
      `${pageNum} 0 obj\n` +
        `<< /Type /Page /Parent 2 0 R ` +
        `/MediaBox [0 0 ${pageWpt.toFixed(3)} ${pageHpt.toFixed(3)}] ` +
        `/Resources << /XObject << /Im0 ${imgNum} 0 R >> >> ` +
        `/Contents ${contentNum} 0 R >>\n` +
        `endobj\n`
    );

    // ------------------------------------------------------------
    // Page content
    //
    // First draw artwork image.
    // Then draw vector kiss-cut paths.
    // ------------------------------------------------------------
    let content = "";

    content +=
      `q\n` +
      `${pageWpt.toFixed(3)} 0 0 ${pageHpt.toFixed(3)} 0 0 cm\n` +
      `/Im0 Do\n` +
      `Q\n`;

    // Kiss-cut style:
    // 1 0 1 RG = magenta
    // 0.566929... pt = 0.20 mm
    const cutLineWidthPt = (0.20 / 25.4) * 72;

    if (Array.isArray(page.vectorPaths)) {
      for (const cutPath of page.vectorPaths) {
        const pathOps = buildPdfPathOps(cutPath.points);
        if (!pathOps) continue;

        content +=
          `q\n` +
          `1 0 1 RG\n` +
          `${cutLineWidthPt.toFixed(4)} w\n` +
          `1 J\n` + // round line caps
          `1 j\n` + // round line joins
          `${pathOps}\n` +
          `h\n` +
          `S\n` +
          `Q\n`;
      }
    }

    const contentBytes = enc.encode(content);

    offsets[contentNum] = length;

    pushStr(
      `${contentNum} 0 obj\n` +
        `<< /Length ${contentBytes.length} >>\n` +
        `stream\n`
    );

    push(contentBytes);
    pushStr("\nendstream\nendobj\n");

    // ------------------------------------------------------------
    // JPEG image object
    // ------------------------------------------------------------
    offsets[imgNum] = length;

    pushStr(
      `${imgNum} 0 obj\n` +
        `<< /Type /XObject ` +
        `/Subtype /Image ` +
        `/Width ${page.canvas.width} ` +
        `/Height ${page.canvas.height} ` +
        `/ColorSpace /DeviceRGB ` +
        `/BitsPerComponent 8 ` +
        `/Filter /DCTDecode ` +
        `/Length ${jpegBytes.length} >>\n` +
        `stream\n`
    );

    push(jpegBytes);
    pushStr("\nendstream\nendobj\n");
  }

  // --------------------------------------------------------------
  // Cross-reference table
  // --------------------------------------------------------------
  const totalObjs = 2 + totalPages * 3;
  const xrefOffset = length;

  let xref =
    `xref\n` +
    `0 ${totalObjs + 1}\n` +
    `0000000000 65535 f \n`;

  for (let i = 1; i <= totalObjs; i++) {
    xref +=
      String(offsets[i]).padStart(10, "0") +
      " 00000 n \n";
  }

  pushStr(xref);

  pushStr(
    `trailer\n` +
      `<< /Size ${totalObjs + 1} /Root 1 0 R >>\n` +
      `startxref\n` +
      `${xrefOffset}\n` +
      `%%EOF`
  );

  return new Blob(parts, {
    type: "application/pdf",
  });
}

async function importPdfPages(file) {
  if (!file) throw new Error("No PDF file selected.");

  const bytes = new Uint8Array(await file.arrayBuffer());
  if (!bytes.length) throw new Error("The PDF file is empty.");

  let loadingTask;
  try {
    loadingTask = pdfjsLib.getDocument({
      data: bytes,
      useWorkerFetch: true,
      isEvalSupported: true,
      disableAutoFetch: false,
      disableStream: false,
    });
    const pdf = await loadingTask.promise;
    if (!pdf.numPages) throw new Error("The PDF contains no pages.");

    const pages = [];
    for (let pageNo = 1; pageNo <= pdf.numPages; pageNo++) {
      const page = await pdf.getPage(pageNo);
      const base = page.getViewport({ scale: 1 });
      const longest = Math.max(base.width, base.height);
      const scale = Math.min(3, 1800 / Math.max(1, longest));
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));
      const ctx = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport, background: "#ffffff" }).promise;

      const url = canvas.toDataURL("image/png");
      const element = new Image();
      element.decoding = "async";
      element.src = url;
      await new Promise((resolve, reject) => {
        element.onload = resolve;
        element.onerror = () => reject(new Error(`Could not render PDF page ${pageNo}.`));
      });

      pages.push({
        element,
        url,
        width: element.naturalWidth || canvas.width,
        height: element.naturalHeight || canvas.height,
        name: `${file.name} — page ${pageNo}`,
        sourcePdf: true,
        pageNo,
      });
    }
    return pages;
  } catch (err) {
    try { loadingTask?.destroy?.(); } catch (_) {}
    throw new Error(`PDF could not be loaded: ${err?.message || String(err)}`);
  }
}

/* =========================================================================
   UI
   ========================================================================= */

const CUT_COLOR = "#FF00FF";
let ID_SEQ = 1;

function Slider({ label, value, onChange, min, max, step, unit, hint }) {
  return (
    <div className="ksc-field">
      <div className="ksc-field-row">
        <span className="ksc-field-label">{label}</span>
        <span className="ksc-field-value">
          {value}
          <span className="ksc-field-unit">{unit}</span>
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="ksc-slider"
      />
      {hint && <div className="ksc-field-hint">{hint}</div>}
    </div>
  );
}

export default function StickerKissCutStudio() {
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]); // [{id, element, url, width, height, name}]
  const [activeIndex, setActiveIndex] = useState(0);
  const [results, setResults] = useState({}); // id -> {status, data, error}
  const [showArtworkOutline, setShowArtworkOutline] = useState(false);
  const [animateCut, setAnimateCut] = useState(true);
  const [viewMode, setViewMode] = useState("single"); // single | sheet
  const [shape, setShape] = useState("rect"); // rect | round
  const [printType, setPrintType] = useState("digital"); // digital | offset
  const [layoutMode, setLayoutMode] = useState("combined"); // combined | onePerPage (only matters with 2+ images)
  const [cutMode, setCutMode] = useState("auto"); // auto | manual
  const [manualPoints, setManualPoints] = useState({});
  const [isDrawing, setIsDrawing] = useState(false);

  const [params, setParams] = useState({
    offsetMm: 0.0,
    smoothing: 0,
    joinDistance: 0,
    bgThreshold: 50,
    dpi: 298,
    borderMargin: 0,
  });

  const setParam = (key) => (val) => setParams((p) => ({ ...p, [key]: val }));

  const activeImage = images[activeIndex] || null;
  const activeResult = activeImage ? results[activeImage.id] : null;

  const gridConfig = GRID_CONFIGS[shape][printType];

  const handleFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    (async () => {
      for (const file of files) {
        if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
          try {
            const pages = await importPdfPages(file);
            if (!pages.length) throw new Error("No pages were rendered from the PDF.");
            setImages((prev) => [
              ...prev,
              ...pages.map((p) => ({ id: `pdf-${ID_SEQ++}`, ...p })),
            ]);
          } catch (err) {
            window.alert(`PDF upload failed: ${err?.message || err}`);
          }
          continue;
        }
        if (!/^image\/(png|jpeg|webp)$/.test(file.type)) continue;
        const url = URL.createObjectURL(file);
        const element = new Image();
        const id = `img-${ID_SEQ++}`;
        element.onload = () => {
          setImages((prev) => [...prev, { id, element, url, width: element.naturalWidth, height: element.naturalHeight, name: file.name }]);
        };
        element.onerror = () => setResults((prev) => ({ ...prev, [id]: { status: "error", error: "Could not load that image file." } }));
        element.src = url;
      }
    })();
  }, []);
  const removeImage = (id) => {
    setImages((prev) => prev.filter((im) => im.id !== id));
    setResults((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setManualPoints((prev) => { const next = { ...prev }; delete next[id]; return next; });
    setActiveIndex((i) => Math.max(0, i - 1));
  };

  // regenerate every image whenever the image set or shared params change
  useEffect(() => {
    if (!images.length) return;
    let cancelled = false;
    setResults((prev) => {
      const next = { ...prev };
      images.forEach((im) => {
        next[im.id] = { status: "processing" };
      });
      return next;
    });
    const t = setTimeout(() => {
      if (cancelled) return;
      const next = {};
      images.forEach((im) => {
        try {
          const data = buildRender(im, params);
          const mp = manualPoints[im.id];
          if (cutMode === "manual" && Array.isArray(mp) && mp.length >= 3) {
            // Manual points are stored normalized (0..1), so changing the
            // border margin/crop never moves the user's drawing.
            const manualRaw = mp.map(([nx, ny]) => [nx * data.width, ny * data.height]);
            const manual = simplifyClosed(manualRaw, Math.max(0.5, params.smoothing));
            const offPx = (params.offsetMm / 25.4) * params.dpi;
            const off = offsetPolygon(manual, offPx);
            data.pathData = polygonToPath(off);
            data.offsetPointsImgSpace = off;
            data.artworkPointsImgSpace = manual;
            data.pointCount = off.length;
            data.mode = "MANUAL";
          }
          next[im.id] = { status: "ready", data };
        } catch (err) {
          next[im.id] = { status: "error", error: err.message || "Kiss-cut generation failed." };
        }
      });
      setResults(next);
    }, 30);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    images,
    params.offsetMm,
    params.smoothing,
    params.joinDistance,
    params.bgThreshold,
    params.dpi,
    params.borderMargin,
    cutMode,
    manualPoints,
  ]);

  useEffect(() => {
    if (activeIndex >= images.length) setActiveIndex(Math.max(0, images.length - 1));
  }, [images.length, activeIndex]);

  const goPrev = () => setActiveIndex((i) => (images.length ? (i - 1 + images.length) % images.length : 0));
  const goNext = () => setActiveIndex((i) => (images.length ? (i + 1) % images.length : 0));

  const reset = () => {
    setParams({ offsetMm: 0.0, smoothing: 0, joinDistance: 0, bgThreshold: 50, dpi: 298, borderMargin: 0 });
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPDF = async () => {
    if (!activeImage || activeResult?.status !== "ready" || !activeResult.data?.canvas) {
      return;
    }
    setPdfBusy(true);
    try {
      const blob = await buildKissCutPdf(activeResult.data, params);
      if (!(blob instanceof Blob) || blob.size < 100) {
        throw new Error("The PDF export produced an empty file.");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kiss-cut-${(activeImage.name || "artwork").replace(/\.[^.]+$/, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [activeImage.id]: {
          status: "error",
          error: "PDF export failed: " + (err?.message || err),
        },
      }));
    } finally {
      setPdfBusy(false);
    }
  };

  const readyImages = images.filter((im) => results[im.id]?.status === "ready");
  const readyCount = readyImages.length;

  // pages this sheet download will produce, given the current layout mode
  const sheetPages = useMemo(() => {
    if (!readyImages.length) return [];
    if (readyImages.length === 1 || layoutMode === "combined") {
      return [{ imgs: readyImages, config: gridConfig, shape }];
    }
    // one page per image, each page's single image fills every grid slot
    return readyImages.map((im) => ({ imgs: [im], config: gridConfig, shape }));
  }, [readyImages, layoutMode, gridConfig, shape]);

  const getSvgPoint = useCallback((event) => {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    if (!activeImage) return null;
    return [
      Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
    ];
  }, [activeImage, activeResult]);

  const startManualDraw = useCallback((event) => {
    if (cutMode !== "manual" || !activeImage) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const p = getSvgPoint(event);
    if (!p) return;
    setIsDrawing(true);
    setManualPoints((prev) => ({ ...prev, [activeImage.id]: [p] }));
  }, [cutMode, activeImage, getSvgPoint]);

  const continueManualDraw = useCallback((event) => {
    if (!isDrawing || cutMode !== "manual" || !activeImage) return;
    const p = getSvgPoint(event);
    if (!p) return;
    setManualPoints((prev) => {
      const current = prev[activeImage.id] || [];
      const last = current[current.length - 1];
      if (last && Math.hypot(p[0] - last[0], p[1] - last[1]) < 2) return prev;
      return { ...prev, [activeImage.id]: [...current, p] };
    });
  }, [isDrawing, cutMode, activeImage, getSvgPoint]);

  const finishManualDraw = useCallback(() => setIsDrawing(false), []);
  const clearManualDraw = () => { if (activeImage) setManualPoints((prev) => ({ ...prev, [activeImage.id]: [] })); };

  const { counts: sheetCounts } = useMemo(
    () => distributeSlots(readyImages.map((im) => im.id), gridConfig.TOTAL_LABELS),
    [readyImages, gridConfig]
  );

  const sheetSvgMarkup = useMemo(() => {
    if (viewMode !== "sheet" || !images.length) return "";
    // preview always shows the combined single-page layout for the active tab
    if (sheetPages.length && layoutMode === "onePerPage" && readyImages.length > 1) {
      const previewIdx = Math.min(activeIndex, sheetPages.length - 1);
      return buildSheetSVG(sheetPages[previewIdx].imgs, results, gridConfig, shape, params);
    }
    return buildSheetSVG(images, results, gridConfig, shape, params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, images, results, gridConfig, shape, params, sheetPages, layoutMode, readyImages.length, activeIndex]);

  const [sheetPdfBusy, setSheetPdfBusy] = useState(false);
  const downloadSheetPDF = async () => {
    if (!sheetPages.length) return;
    setSheetPdfBusy(true);
    try {
      const blob = await buildSheetPagesPdf(sheetPages, results, params);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const suffix = sheetPages.length > 1 ? `-${sheetPages.length}pages` : "";
      a.download = `sheet-${shape}-${printType}${suffix}.pdf`;
      a.click();
    } catch (err) {
      console.error(err);
    } finally {
      setSheetPdfBusy(false);
    }
  };

  const passCount = activeResult?.status === "ready" ? activeResult.data.quality.filter((q) => q.pass).length : 0;
  const status = !activeImage
    ? "empty"
    : !activeResult
    ? "processing"
    : activeResult.status;

  return (
    <div className="ksc-root">
      <style>{`
        .ksc-root {
          --ink: #16181d;
          --paper: #f6f5f2;
          --panel: #ffffff;
          --line: #e2e0da;
          --muted: #7a7a72;
          --cut: ${CUT_COLOR};
          --ok: #1a8a5f;
          --warn: #c23b3b;
          --mono: ui-monospace, "SF Mono", "Roboto Mono", Menlo, Consolas, monospace;
          --sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Helvetica, Arial, sans-serif;
          font-family: var(--sans);
          color: var(--ink);
          background: var(--paper);
          display: grid;
          grid-template-columns: minmax(0, 1.5fr) 320px;
          gap: 1px;
          background-color: var(--line);
          border: 1px solid var(--line);
          border-radius: 14px;
          overflow: hidden;
          min-height: 640px;
        }
        @media (max-width: 860px) {
          .ksc-root { grid-template-columns: 1fr; }
        }

        .ksc-stage {
          background: #0f1013;
          background-image:
            linear-gradient(45deg, #16171b 25%, transparent 25%),
            linear-gradient(-45deg, #16171b 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #16171b 75%),
            linear-gradient(-45deg, transparent 75%, #16171b 75%);
          background-size: 22px 22px;
          background-position: 0 0, 0 11px, 11px -11px, -11px 0px;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .ksc-stage-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          border-bottom: 1px solid #23252b;
          gap: 10px;
          flex-wrap: wrap;
        }
        .ksc-brand {
          display: flex;
          align-items: center;
          gap: 9px;
          color: #f2f2ee;
          font-size: 13px;
          letter-spacing: 0.02em;
        }
        .ksc-mode-badge {
          font-family: var(--mono);
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 999px;
          border: 1px solid #3a3c44;
          color: #b9bac2;
        }
        .ksc-mode-badge.on {
          border-color: var(--cut);
          color: var(--cut);
        }
        .ksc-view-toggle {
          display: flex;
          gap: 4px;
          background: #1c1e24;
          border: 1px solid #32343c;
          border-radius: 8px;
          padding: 3px;
        }
        .ksc-view-toggle button {
          background: none;
          border: none;
          color: #9fa0a8;
          font-size: 11.5px;
          padding: 5px 10px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .ksc-view-toggle button.active {
          background: var(--cut);
          color: #1a001a;
          font-weight: 600;
        }

        .ksc-canvas-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 28px;
          min-height: 0;
          position: relative;
        }
        .ksc-image-box {
          position: relative;
          max-width: 100%;
          max-height: 100%;
          box-shadow: 0 30px 60px -20px rgba(0,0,0,0.65);
        }
        .ksc-image-box img, .ksc-image-box svg {
          display: block;
          width: 100%;
          height: auto;
        }
        .ksc-image-box svg {
          position: absolute;
          inset: 0;
        }
        .ksc-cutline {
          stroke-dasharray: 6 5;
        }
        .ksc-cutline.animate {
          animation: ksc-march 0.9s linear infinite;
        }
        @keyframes ksc-march {
          to { stroke-dashoffset: -22; }
        }
        .ksc-artwork-outline {
          stroke-dasharray: 2 3;
          opacity: 0.55;
        }

        .ksc-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: rgba(28,30,36,0.85);
          border: 1px solid #3a3c44;
          color: #f2f2ee;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 5;
        }
        .ksc-nav-btn:hover { background: rgba(255,0,255,0.18); border-color: var(--cut); }
        .ksc-nav-btn.left { left: 10px; }
        .ksc-nav-btn.right { right: 10px; }

        .ksc-counter {
          position: absolute;
          top: 10px;
          left: 50%;
          transform: translateX(-50%);
          font-family: var(--mono);
          font-size: 11px;
          color: #e7e7ea;
          background: rgba(28,30,36,0.85);
          border: 1px solid #3a3c44;
          padding: 4px 10px;
          border-radius: 999px;
          z-index: 5;
        }
        .ksc-remove-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: rgba(28,30,36,0.85);
          border: 1px solid #3a3c44;
          color: #ff7676;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 5;
        }
        .ksc-remove-btn:hover { border-color: #ff7676; }

        .ksc-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          color: #cfcfd6;
          text-align: center;
          padding: 40px;
        }
        .ksc-upload-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--cut);
          color: #1a001a;
          font-weight: 600;
          font-size: 13px;
          border: none;
          padding: 10px 18px;
          border-radius: 999px;
          cursor: pointer;
        }
        .ksc-upload-btn:hover { filter: brightness(1.08); }

        .ksc-sheet-wrap {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ksc-sheet-wrap svg {
          max-width: 100%;
          max-height: 100%;
          background: #fff;
          box-shadow: 0 30px 60px -20px rgba(0,0,0,0.65);
        }

        .ksc-stage-bottom {
          border-top: 1px solid #23252b;
          padding: 12px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .ksc-status {
          font-family: var(--mono);
          font-size: 11.5px;
          color: #9fa0a8;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .ksc-status.processing { color: var(--cut); }
        .ksc-status.error { color: #ff7676; }
        .ksc-spin { animation: ksc-spin 0.9s linear infinite; }
        @keyframes ksc-spin { to { transform: rotate(360deg); } }
        .ksc-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .ksc-icon-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #1c1e24;
          color: #e7e7ea;
          border: 1px solid #32343c;
          font-size: 12px;
          padding: 7px 11px;
          border-radius: 8px;
          cursor: pointer;
        }
        .ksc-icon-btn:hover { background: #262832; }
        .ksc-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .ksc-icon-btn.primary { background: var(--cut); color: #1a001a; border-color: var(--cut); font-weight: 600; }

        .ksc-panel {
          background: var(--panel);
          padding: 18px 18px 24px;
          overflow-y: auto;
        }
        .ksc-panel-header {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--muted);
          font-weight: 700;
          margin: 4px 0 14px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ksc-field { margin-bottom: 16px; }
        .ksc-field-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 6px;
        }
        .ksc-field-label { font-size: 12.5px; color: var(--ink); }
        .ksc-field-value { font-family: var(--mono); font-size: 12.5px; color: var(--muted); }
        .ksc-field-unit { margin-left: 2px; color: #b3b1a8; }
        .ksc-field-hint { font-size: 10.5px; color: #a4a297; margin-top: 3px; }
        .ksc-slider {
          -webkit-appearance: none;
          width: 100%;
          height: 3px;
          border-radius: 2px;
          background: var(--line);
          outline: none;
        }
        .ksc-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--cut);
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px var(--cut);
        }
        .ksc-toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12.5px;
          padding: 9px 0;
          border-top: 1px solid var(--line);
        }
        .ksc-switch {
          width: 32px;
          height: 18px;
          border-radius: 999px;
          background: var(--line);
          position: relative;
          cursor: pointer;
          border: none;
          flex-shrink: 0;
        }
        .ksc-switch.on { background: var(--cut); }
        .ksc-switch-knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #fff;
          transition: transform 0.15s ease;
        }
        .ksc-switch.on .ksc-switch-knob { transform: translateX(14px); }

        .ksc-segmented {
          display: flex;
          gap: 6px;
          margin-bottom: 16px;
        }
        .ksc-segmented button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 12px;
          padding: 8px 6px;
          border-radius: 8px;
          border: 1px solid var(--line);
          background: var(--panel);
          color: var(--muted);
          cursor: pointer;
        }
        .ksc-segmented button.active {
          border-color: var(--cut);
          color: var(--cut);
          background: #fff0fb;
          font-weight: 600;
        }

        .ksc-sheet-summary {
          font-size: 12px;
          color: var(--ink);
          background: var(--paper);
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 10px 12px;
          margin-bottom: 16px;
        }
        .ksc-sheet-summary-row {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
          font-family: var(--mono);
          font-size: 11.5px;
          color: var(--muted);
        }
        .ksc-sheet-summary-row b { color: var(--ink); font-weight: 600; }

        .ksc-thumbstrip {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 16px;
        }
        .ksc-thumb {
          width: 44px;
          height: 44px;
          border-radius: 6px;
          border: 2px solid var(--line);
          overflow: hidden;
          cursor: pointer;
          background: #fff;
          position: relative;
        }
        .ksc-thumb.active { border-color: var(--cut); }
        .ksc-thumb img { width: 100%; height: 100%; object-fit: cover; }

        .ksc-quality {
          margin-top: 4px;
          border-top: 1px solid var(--line);
          padding-top: 14px;
        }
        .ksc-quality-summary {
          font-family: var(--mono);
          font-size: 12px;
          margin-bottom: 10px;
          color: var(--muted);
        }
        .ksc-quality-item {
          display: flex;
          align-items: flex-start;
          gap: 7px;
          font-size: 12px;
          padding: 4px 0;
          color: var(--ink);
        }
        .ksc-quality-item.fail { color: var(--warn); }
        .ksc-quality-item svg { flex-shrink: 0; margin-top: 1px; }

        .ksc-reset-row { margin-top: 18px; display: flex; }
        .ksc-reset-row button {
          font-size: 12px;
          color: var(--muted);
          background: none;
          border: 1px solid var(--line);
          padding: 7px 12px;
          border-radius: 8px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .ksc-reset-row button:hover { border-color: #c9c7bd; color: var(--ink); }
      `}</style>

      {/* ---------------- STAGE ---------------- */}
      <div className="ksc-stage">
        <div className="ksc-stage-top">
          <div className="ksc-brand">
            <ScanLine size={15} color={CUT_COLOR} />
            Kiss-Cut Studio
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {viewMode === "single" && activeResult?.status === "ready" && (
              <span className={`ksc-mode-badge on`}>
                {activeResult.data.mode === "ALPHA" ? "ALPHA MASK" : "BACKGROUND REMOVAL"}
              </span>
            )}
            {images.length > 0 && (
              <div className="ksc-view-toggle">
                <button className={viewMode === "single" ? "active" : ""} onClick={() => setViewMode("single")}>
                  <ScanLine size={12} /> Single
                </button>
                <button className={viewMode === "sheet" ? "active" : ""} onClick={() => setViewMode("sheet")}>
                  <Grid3x3 size={12} /> Sheet
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="ksc-canvas-wrap">
          {!images.length && (
            <div className="ksc-empty">
              <Layers size={26} color="#5c5d66" />
              <div style={{ fontSize: 13.5, maxWidth: 280, lineHeight: 1.5 }}>
                Upload sticker artwork — PNG/JPEG/WebP or PDF. PDF pages are rendered for editing and the final kiss-cut is exported as a separate vector path. Upload one image to fill the whole sheet, or several
                to split the sheet between them. One outer kiss-cut path is
                generated per image automatically.
              </div>
              <button className="ksc-upload-btn" onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} /> Upload artwork
              </button>
            </div>
          )}

          {images.length > 0 && viewMode === "single" && activeImage && (
            <>
              {images.length > 1 && (
                <span className="ksc-counter">
                  Image {activeIndex + 1} / {images.length}
                </span>
              )}
              {images.length > 1 && (
                <button className="ksc-nav-btn left" onClick={goPrev} aria-label="Previous image">
                  <ChevronLeft size={18} />
                </button>
              )}
              {images.length > 1 && (
                <button className="ksc-nav-btn right" onClick={goNext} aria-label="Next image">
                  <ChevronRight size={18} />
                </button>
              )}
              <button className="ksc-remove-btn" onClick={() => removeImage(activeImage.id)} aria-label="Remove image">
                <X size={13} />
              </button>
              <div
                className="ksc-image-box"
                style={{
                  aspectRatio: `${activeResult?.status === "ready" ? activeResult.data.width : activeImage.width} / ${
                    activeResult?.status === "ready" ? activeResult.data.height : activeImage.height
                  }`,
                }}
              >
                <img src={activeResult?.status === "ready" ? activeResult.data.url : activeImage.url} alt={activeImage.name} />
                {activeResult?.status === "ready" && (
                  <svg viewBox={`0 0 ${activeResult.data.width} ${activeResult.data.height}`}>
                    {showArtworkOutline && (
                      <path
                        d={activeResult.data.artworkPathData}
                        className="ksc-artwork-outline"
                        fill="none"
                        stroke="#00e5ff"
                        strokeWidth={Math.max(1, activeResult.data.width / 400)}
                      />
                    )}
                    {cutMode === "manual" && (manualPoints[activeImage.id]?.length || 0) >= 2 && (
                      <path
                        d={polygonToPath((manualPoints[activeImage.id] || []).map(([nx, ny]) => [nx * activeResult.data.width, ny * activeResult.data.height]))}
                        fill="none"
                        stroke="#00e5ff"
                        strokeWidth={Math.max(1.5, activeResult.data.width / 260)}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                    {cutMode === "auto" && (
                      <path
                        d={activeResult.data.pathData}
                        className={`ksc-cutline ${animateCut ? "animate" : ""}`}
                        fill="none"
                        stroke={CUT_COLOR}
                        strokeWidth={Math.max(1.5, activeResult.data.width / 260)}
                      />
                    )}
                    {cutMode === "manual" && (
                      <rect x="0" y="0" width="100%" height="100%" fill="transparent"
                        style={{ pointerEvents: "all" }}
                        onPointerDown={startManualDraw}
                        onPointerMove={continueManualDraw}
                        onPointerUp={finishManualDraw}
                        onPointerCancel={finishManualDraw}
                      />
                    )}
                  </svg>
                )}
              </div>
            </>
          )}

          {images.length > 0 && viewMode === "sheet" && (
            <div className="ksc-sheet-wrap" dangerouslySetInnerHTML={{ __html: sheetSvgMarkup }} />
          )}
        </div>

        <div className="ksc-stage-bottom">
          <div className={`ksc-status ${status}`}>
            {status === "empty" && "No artwork loaded"}
            {status === "processing" && (
              <>
                <RefreshCw size={12} className="ksc-spin" /> generating cut path…
              </>
            )}
            {status === "ready" && viewMode === "single" &&
              `contour points: ${activeResult.data.pointCount} · ${passCount}/${activeResult.data.quality.length} checks passed`}
            {status === "ready" && viewMode === "sheet" &&
              `${gridConfig.GRID_COLS}×${gridConfig.GRID_ROWS} sheet · ${gridConfig.TOTAL_LABELS} labels · ${readyCount}/${images.length} artworks ready${
                readyImages.length > 1 ? ` · ${sheetPages.length} page${sheetPages.length > 1 ? "s" : ""}` : ""
              }`}
            {status === "error" && `⚠ ${activeResult?.error}`}
          </div>
          <div className="ksc-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFiles(e.target.files)}
            />
            <button className="ksc-icon-btn" onClick={() => fileInputRef.current?.click()}>
              <Upload size={13} /> {images.length ? "Add image(s)" : "Upload"}
            </button>
            {viewMode === "single" && (
              <button className="ksc-icon-btn primary" disabled={activeResult?.status !== "ready" || pdfBusy} onClick={downloadPDF}>
                <Download size={13} /> {pdfBusy ? "Building PDF…" : "Download PDF"}
              </button>
            )}
            {viewMode === "sheet" && (
              <button className="ksc-icon-btn primary" disabled={!readyCount || sheetPdfBusy} onClick={downloadSheetPDF}>
                <Download size={13} />{" "}
                {sheetPdfBusy
                  ? "Building PDF…"
                  : `Download Sheet PDF${sheetPages.length > 1 ? ` (${sheetPages.length}pg)` : ""}`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- CONTROL RAIL ---------------- */}
      <div className="ksc-panel">
        {images.length > 1 && (
          <>
            <div className="ksc-panel-header">
              <Layers size={13} /> Uploaded artwork
            </div>
            <div className="ksc-thumbstrip">
              {images.map((im, i) => (
                <div
                  key={im.id}
                  className={`ksc-thumb ${i === activeIndex ? "active" : ""}`}
                  onClick={() => {
                    setActiveIndex(i);
                    setViewMode("single");
                  }}
                  title={im.name}
                >
                  <img src={im.url} alt={im.name} />
                </div>
              ))}
            </div>
          </>
        )}

        <div className="ksc-panel-header">
          <Grid3x3 size={13} /> Print sheet
        </div>
        <div className="ksc-segmented">
          <button className={shape === "rect" ? "active" : ""} onClick={() => setShape("rect")}>
            <Square size={13} /> Rectangular
          </button>
          <button className={shape === "round" ? "active" : ""} onClick={() => setShape("round")}>
            <CircleIcon size={13} /> Round
          </button>
        </div>
        <div className="ksc-segmented">
          <button className={printType === "digital" ? "active" : ""} onClick={() => setPrintType("digital")}>
            Digital
          </button>
          <button className={printType === "offset" ? "active" : ""} onClick={() => setPrintType("offset")}>
            Offset
          </button>
        </div>

        {images.length > 1 && (
          <>
            <div className="ksc-panel-header">Multiple images</div>
            <div className="ksc-segmented">
              <button className={layoutMode === "combined" ? "active" : ""} onClick={() => setLayoutMode("combined")}>
                Split on one page
              </button>
              <button className={layoutMode === "onePerPage" ? "active" : ""} onClick={() => setLayoutMode("onePerPage")}>
                One page per image
              </button>
            </div>
            <div className="ksc-field-hint" style={{ marginTop: -10, marginBottom: 16 }}>
              {layoutMode === "combined"
                ? "All images share the grid on a single sheet page."
                : "Each image fills every grid slot on its own page — the PDF will have one page per image."}
            </div>
          </>
        )}

        <div className="ksc-sheet-summary">
          <div className="ksc-sheet-summary-row">
            <span>Grid</span>
            <b>
              {gridConfig.GRID_COLS} × {gridConfig.GRID_ROWS}
            </b>
          </div>
          <div className="ksc-sheet-summary-row">
            <span>Sheet size</span>
            <b>
              {gridConfig.PDF_WIDTH} × {gridConfig.PDF_HEIGHT} mm
            </b>
          </div>
          <div className="ksc-sheet-summary-row">
            <span>Label size</span>
            <b>{shape === "round" ? `⌀ ${gridConfig.STICKER_DIAMETER} mm` : `${gridConfig.GRID_WIDTH} × ${gridConfig.GRID_HEIGHT} mm`}</b>
          </div>
          <div className="ksc-sheet-summary-row">
            <span>Total labels</span>
            <b>{gridConfig.TOTAL_LABELS}</b>
          </div>
          {images.length === 1 && readyCount === 1 && (
            <div style={{ borderTop: "1px solid var(--line)", marginTop: 6, paddingTop: 6 }}>
              <div className="ksc-sheet-summary-row">
                <span>{images[0].name.length > 16 ? images[0].name.slice(0, 14) + "…" : images[0].name}</span>
                <b>fills all {gridConfig.TOTAL_LABELS} labels</b>
              </div>
            </div>
          )}
          {layoutMode === "combined" && sheetCounts.length > 1 && (
            <div style={{ borderTop: "1px solid var(--line)", marginTop: 6, paddingTop: 6 }}>
              {sheetCounts.map(({ id, count }, i) => {
                const im = images.find((x) => x.id === id);
                return (
                  <div className="ksc-sheet-summary-row" key={id}>
                    <span>{im?.name ? (im.name.length > 16 ? im.name.slice(0, 14) + "…" : im.name) : `Image ${i + 1}`}</span>
                    <b>{count} labels</b>
                  </div>
                );
              })}
            </div>
          )}
          {layoutMode === "onePerPage" && readyImages.length > 1 && (
            <div style={{ borderTop: "1px solid var(--line)", marginTop: 6, paddingTop: 6 }}>
              {readyImages.map((im, i) => (
                <div className="ksc-sheet-summary-row" key={im.id}>
                  <span>Page {i + 1}: {im.name.length > 14 ? im.name.slice(0, 12) + "…" : im.name}</span>
                  <b>{gridConfig.TOTAL_LABELS} labels</b>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ksc-panel-header">
          <Ruler size={13} /> Kiss cut
        </div>

        <div className="ksc-segmented">
          <button className={cutMode === "auto" ? "active" : ""} onClick={() => setCutMode("auto")}>Auto Detect</button>
          <button className={cutMode === "manual" ? "active" : ""} onClick={() => setCutMode("manual")}>Draw Manually</button>
        </div>
        {cutMode === "manual" && (
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            <button className="ksc-icon-btn" onClick={clearManualDraw}>Clear drawing</button>
            <div className="ksc-field-hint" style={{ alignSelf: "center" }}>Draw a closed outline on the PDF/artwork; Offset is applied after drawing.</div>
          </div>
        )}

        <Slider
          label="Offset"
          value={params.offsetMm}
          onChange={setParam("offsetMm")}
          min={-8}
          max={8}
          step={0.1}
          unit="mm"
          hint="Positive = outside artwork · 0 = exact artwork edge · negative = move the cut inward."
        />
        <Slider
          label="Contour smoothing"
          value={params.smoothing}
          onChange={setParam("smoothing")}
          min={0}
          max={6}
          step={0.5}
          unit=""
          hint="Low keeps fine detail; high simplifies corners into a cleaner cut path."
        />
        <Slider
          label="Join artwork"
          value={params.joinDistance}
          onChange={setParam("joinDistance")}
          min={0}
          max={26}
          step={1}
          unit="px"
          hint="Morphological closing distance — merges separate letters/decorations into one silhouette."
        />
        <Slider
          label="Border margin"
          value={params.borderMargin}
          onChange={setParam("borderMargin")}
          min={0}
          max={10}
          step={0.5}
          unit="mm"
          hint="Real-world white space kept between the cut line and the trimmed edge of the canvas — same unit as offset, so it stays visible regardless of upload resolution."
        />
        <Slider
          label="Print resolution"
          value={params.dpi}
          onChange={setParam("dpi")}
          min={72}
          max={600}
          step={1}
          unit="dpi"
          hint="Used to convert the mm offset and margin into pixels, and to size exported files."
        />
        {activeResult?.status === "ready" && activeResult.data.mode === "BACKGROUND" && (
          <Slider
            label="Background threshold"
            value={params.bgThreshold}
            onChange={setParam("bgThreshold")}
            min={10}
            max={140}
            step={5}
            unit=""
            hint="How different a pixel must be from the corner color to count as artwork."
          />
        )}

        <div className="ksc-toggle-row">
          <span>Show artwork outline</span>
          <button
            className={`ksc-switch ${showArtworkOutline ? "on" : ""}`}
            onClick={() => setShowArtworkOutline((v) => !v)}
          >
            <span className="ksc-switch-knob" />
          </button>
        </div>
        <div className="ksc-toggle-row">
          <span>Animate cut line</span>
          <button className={`ksc-switch ${animateCut ? "on" : ""}`} onClick={() => setAnimateCut((v) => !v)}>
            <span className="ksc-switch-knob" />
          </button>
        </div>

        <div className="ksc-reset-row">
          <button onClick={reset}>
            <RotateCcw size={12} /> Reset settings
          </button>
        </div>

        {viewMode === "single" && activeResult?.status === "ready" && (
          <div className="ksc-quality">
            <div className="ksc-panel-header" style={{ marginBottom: 8 }}>
              Quality check
            </div>
            <div className="ksc-quality-summary">
              {passCount}/{activeResult.data.quality.length} passed
              {!activeResult.data.ok && " — adjust threshold / join distance"}
            </div>
            {activeResult.data.quality.map((q) => (
              <div key={q.label} className={`ksc-quality-item ${q.pass ? "" : "fail"}`}>
                {q.pass ? (
                  <CheckCircle2 size={13} color="#1a8a5f" />
                ) : (
                  <XCircle size={13} color="#c23b3b" />
                )}
                {q.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}